import * as React from 'react';
import DashboardPageLayout from 'src/frontend-common/components/DashboardPageLayout';
import { Divider, styled, Tab, Tabs } from '@mui/material';
import Column from 'src/frontend-common/components/Column';
import SnackBarCustom from 'src/frontend-common/components/SnackbarCustom';
import MyProfileTab from './components/MyProfile';
import CompanyInfo from './components/CompanyInfo';
import BillingInfo from './components/BillingInfo/index';
import TeamInfo from './components/TeamInfo/index';
import NotificationsInfo from './components/NotificationsInfo/index';

import { useQuery } from 'src/hooks';
import { toggleForceLoading } from 'src/redux/slices/creditSlice';
import { useDispatch } from 'react-redux';
import { useLazyGetAvailableBudgetQuery } from 'src/redux/services/credits';
import Security from './components/Security';
import { useLocation, useNavigate } from 'react-router-dom';

type TabId = 'myProfile' | 'company' | 'billing' | 'team' | 'notifications' | 'security';
interface Tab {
  label: string;
  id: TabId;
}

const tabs = [
  {
    label: 'General',
    id: 'myProfile',
  },
  {
    label: 'Company',
    id: 'company',
  },
  {
    label: 'Billing',
    id: 'billing',
  },
  {
    label: 'Team',
    id: 'team',
  },
  {
    label: 'Notifications',
    id: 'notifications',
  },
  {
    label: 'Security',
    id: 'security',
  },
] as const;

const SettingsPage: React.FC = () => {
  const { stripeSuccess } = useQuery();
  const [stripeSuccessState, setStripeSuccessState] = React.useState<boolean>(false);
  const [showSnackBar, setShowSnackBar] = React.useState(stripeSuccess === 'true');
  const [activeTab, setActiveTab] = React.useState<number>(stripeSuccess ? 2 : 0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };
  const [trigger] = useLazyGetAvailableBudgetQuery();

  const params = useLocation();

  React.useEffect(() => {
    const locationState = params.state as any;
    if (locationState && locationState.tab) {
      setActiveTab(locationState.tab);
      navigate('', { replace: true });
    }
    if (locationState && locationState.tab === 0) {
      setActiveTab(locationState.tab);
      navigate('', { replace: true });
    }
  }, [params]);
  React.useEffect(() => {
    setStripeSuccessState(stripeSuccess === 'true');
    if (stripeSuccess && stripeSuccess === 'true') {
      triggerUpdateBalance();
    }
  }, [stripeSuccess]);

  const triggerUpdateBalance = () => {
    dispatch(toggleForceLoading());
    return setTimeout(() => trigger(), 5 * 1000);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTabChange = (_event: unknown, newValue: number) => {
    setActiveTab(newValue);
    setStripeSuccessState(false);
  };

  return (
    <DashboardPageLayout title="Account Settings ">
      <Column data-cy="settings">
        <TabsWrapper value={activeTab} indicatorColor="primary" onChange={handleTabChange}>
          {tabs.map((t, i) => (
            <CustomTab label={t.label} key={t.id} {...a11yProps(i)} />
          ))}
        </TabsWrapper>

        <Divider />
        <>
          {activeTab === 0 && !stripeSuccessState && (
            <TabPanel>
              <MyProfileTab />
            </TabPanel>
          )}
          {activeTab === 1 && !stripeSuccessState && (
            <TabPanel>
              <CompanyInfo />
            </TabPanel>
          )}
          {(activeTab === 2 || stripeSuccessState) && (
            <TabPanel data-testid="billing-info">
              <BillingInfo />
            </TabPanel>
          )}
          {activeTab === 3 && !stripeSuccessState && (
            <TabPanel>
              <TeamInfo />
            </TabPanel>
          )}
          {activeTab === 4 && !stripeSuccessState && (
            <TabPanel>
              <NotificationsInfo />
            </TabPanel>
          )}
          {activeTab === 5 && !stripeSuccessState && (
            <TabPanel>
              <Security />
            </TabPanel>
          )}
        </>
      </Column>
      <SnackBarCustom
        open={showSnackBar}
        data-testid="snackbar"
        autoHideDuration={30000}
        onClose={() => setShowSnackBar(false)}
        severity={stripeSuccessState ? 'success' : 'error'}
        message={stripeSuccessState ? 'Congratulation! You have successfully added funds to your balance.' : ''}
      />
    </DashboardPageLayout>
  );
};

export default SettingsPage;

const TabsWrapper = styled(Tabs)(({ theme }) => ({
  '& div': {
    [theme.breakpoints.down('sm')]: {
      overflowY: 'scroll',
    },
  },
  '& .MuiTabs-indicator': {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
    [theme.breakpoints.up('sm')]: {
      display: 'inherit',
    },
  },
}));

const TabPanel = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(2),
}));

const CustomTab = styled(Tab)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  margin: theme.spacing(0, 1.5),
  width: 'fit-content',
  minWidth: 'fit-content',
  fontSize: 14,
  letterSpacing: 0.11,
  '&:first-of-type': {
    marginLeft: 0,
  },
}));
